// Focus-SKIP mutation sequence — the missing twin of completeFocus.ts.
//
// Beta bug 1 (error log 2026-06-10, live-reproduced): the Today card can be
// pinned by an active-now schedule block with NO linked task (chat-plan /
// manual blocks), in which case `focus.id` IS the block id. The inline
// onSkip handler sent that id to PATCH /api/tasks/{id} → 404 → the block
// stayed open → activeNowBlock re-pinned the same card → "Skip today" looked
// dead (the user's PAUSED screenshot). Skip must branch exactly like
// completeFocusItem: block-id ⇒ close the block; task-id ⇒ PATCH the task
// (the backend skip cascade closes any linked block). Never swallow.

import type { ScheduleBlockLike } from "./completeFocus";

export interface SkipFocusDeps {
  completeScheduleBlock: (id: string) => Promise<unknown>;
  updateTask: (id: string, patch: { status: "skipped" }) => Promise<unknown>;
}

export type SkipFocusResult =
  | { status: "ok" }
  | { status: "error"; error: unknown };

export async function skipFocusItem(
  focusId: string,
  scheduleBlocks: readonly ScheduleBlockLike[],
  deps: SkipFocusDeps
): Promise<SkipFocusResult> {
  const block = scheduleBlocks.find((b) => b.id === focusId);

  try {
    if (block) {
      // The focus is a schedule block. "Skip today" closes the block so the
      // active-now pin releases; a linked task is skipped too.
      await deps.completeScheduleBlock(block.id);
      if (block.task_id) {
        await deps.updateTask(block.task_id, { status: "skipped" });
      }
    } else {
      // A task id — the backend PATCH-skip cascade closes any linked block.
      await deps.updateTask(focusId, { status: "skipped" });
    }
    return { status: "ok" };
  } catch (error) {
    return { status: "error", error };
  }
}
